import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import {
  formatCrossChainDuration,
  isBridgeCrossChainStatus,
} from '@/lib/api/0x-cross-chain/status';

import { useTransactionStore } from '@/store/transaction';
import { CrossChainTransaction } from '@/store/transaction/types';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { XIcon } from '@/ui/Icons/XIcon';

import { classNames } from '@/utils/classnames';
import { routes } from '@/utils/routes';

import styles from './styles.module.css';

const STROKE_COLOR = 'var(--color-special-xchain-stroke)';

const TokenIconElement = ({
  address,
  chainId,
  left = false,
}: {
  address: string;
  chainId: number;
  left?: boolean;
}) => {
  return (
    <div className="relative size-16 flex items-center justify-center border border-token-gray-300 rounded-full">
      <svg
        className={classNames(
          'absolute size-[calc(100%+2px)]',
          left ? undefined : styles.xchainAnimationDelayed,
        )}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox="0 0 100 100"
        shapeRendering="geometricPrecision"
        textRendering="geometricPrecision"
      >
        <defs>
          <linearGradient
            id={`xchain-bot-${left ? 'l' : 'r'}`}
            x1="0"
            y1="0.634111"
            x2="0"
            y2="0.449339"
          >
            <stop offset="17%" stopColor={STROKE_COLOR} />
            <stop offset="97%" stopOpacity={0} stopColor={STROKE_COLOR} />
          </linearGradient>
          <linearGradient
            id={`xchain-top-${left ? 'l' : 'r'}`}
            x1="0"
            y1="0.634111"
            x2="0"
            y2="0.449339"
          >
            <stop offset="0%" stopOpacity={0} stopColor={STROKE_COLOR} />
            <stop offset="90%" stopColor={STROKE_COLOR} />
          </linearGradient>
        </defs>
        <g transform="translate(-70 -50)">
          <g mask={`url(#xchain-mask-bot-${left ? 'l' : 'r'})`}>
            <g transform="translate(120,100) rotate(22)">
              <circle
                className={styles['xchain-circle-bot']}
                r="49.5"
                transform="translate(0,0)"
                fill="none"
                fillOpacity="0"
                stroke={`url(#xchain-bot-${left ? 'l' : 'r'})`}
                strokeOpacity="0"
                strokeLinecap="round"
                strokeDashoffset="-140"
                strokeDasharray="30,311"
              />
            </g>
            <mask
              id={`xchain-mask-bot-${left ? 'l' : 'r'}`}
              x="-150%"
              y="-150%"
              height="400%"
              width="400%"
            >
              <rect
                width="124.617097"
                height="63.444782"
                rx="0"
                ry="0"
                transform="translate(56.555225 100)"
                fill="#d2dbed"
                strokeWidth="0"
              />
            </mask>
          </g>
          <g mask={`url(#xchain-mask-top-${left ? 'l' : 'r'})`}>
            <g transform="translate(120,100) rotate(-22)">
              <circle
                className={styles['xchain-circle-top']}
                r="49.5"
                transform="scale(1,-1) translate(0,0)"
                fill="none"
                fillOpacity="0"
                stroke={`url(#xchain-top-${left ? 'l' : 'r'})`}
                strokeOpacity="0"
                strokeLinecap="round"
                strokeDashoffset="-140"
                strokeDasharray="30,311"
              />
            </g>
            <mask
              id={`xchain-mask-top-${left ? 'l' : 'r'}`}
              x="-150%"
              y="-150%"
              height="400%"
              width="400%"
            >
              <rect
                width="124.096467"
                height="50"
                rx="0"
                ry="0"
                transform="translate(57.951767 50)"
                fill="#d2dbed"
                strokeWidth="0"
              />
            </mask>
          </g>
        </g>
      </svg>
      <TokenAvatar address={address} chainId={chainId} alt="" size={32} />
    </div>
  );
};

const ConnectorElement = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 47 4"
      width={45}
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      <defs>
        <linearGradient id="line-1-fill" x1="1" y1="0.516705" x2="0" y2="0.5">
          <stop offset="0%" stopColor={STROKE_COLOR} />
          <stop offset="100%" stopColor={STROKE_COLOR} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path
        d="M169.5,100h47"
        transform="translate(-169.5 -98)"
        fill="none"
        stroke={STROKE_COLOR}
        strokeOpacity="0.06"
      />
      <g mask="url(#xchain-line-mask)">
        <g transform="translate(-17.244765 1.983575)">
          <rect
            className={styles['xchain-connector-line-1']}
            width="20"
            height="0.983211"
            rx="0.49"
            ry="0.49"
            transform="translate(-3.493558 -0.491605)"
            fill="url(#line-1-fill)"
            fillOpacity="0.3"
            strokeWidth="0"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <mask id="xchain-line-mask" x="-150%" y="-150%" height="400%" width="400%">
          <rect width="47" height="4" rx="0" ry="0" fill="#d2dbed" strokeWidth="0" />
        </mask>
      </g>
    </svg>
  );
};

/** Renders cross-chain progress with caller-owned checkout actions. */
export const PendingCrossChainTransactionView = ({ onDismiss }: { onDismiss: () => void }) => {
  const router = useRouter();
  const currentTransaction = useTransactionStore((state) => state.currentTransaction);
  const transactions = useTransactionStore((state) => state.transactions);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Read the latest snapshot of this tx from the transactions table. The
  // cross-chain poller writes status updates there while `currentTransaction.tx`
  // remains the original submission snapshot.
  const updatedTx = useMemo(() => {
    if (!currentTransaction || !currentTransaction.tx?.hash) return undefined;
    const chainTxs = transactions[currentTransaction.address]?.[currentTransaction.chainId];
    return chainTxs?.find((t) => t.hash === currentTransaction.tx.hash);
  }, [transactions, currentTransaction]);

  const transaction = (updatedTx ?? currentTransaction?.tx) as CrossChainTransaction | undefined;

  const { tradeStatus, sellToken, buyToken, hash, serviceTime, quoteSteps } = transaction ?? {};
  const { status } = tradeStatus ?? {};

  const hasSwapStep = quoteSteps?.some((step) => step.type.toLowerCase() === 'swap');
  const isBridgeOnly = (quoteSteps?.length ?? 0) > 0 && !hasSwapStep;

  const statusCopy =
    isBridgeOnly || isBridgeCrossChainStatus(status)
      ? {
          title: 'Bridging...',
          body: 'Funds are being bridged to the destination chain.',
        }
      : {
          title: 'Confirming transaction...',
          body: 'Transaction is being confirmed on the origin chain.',
        };

  const handleSeeDetails = () => {
    if (!sellToken?.chainId || !hash) return;
    router.push({
      pathname: routes.TX,
      query: { chainId: String(sellToken.chainId), txHash: hash },
    });
  };

  useEffect(() => {
    const secondsInterval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(secondsInterval);
  }, []);

  const remainingLabel = formatCrossChainDuration(serviceTime, true);
  const elapsedLabel = formatCrossChainDuration(elapsedSeconds);

  if (!transaction || !sellToken || !buyToken) return null;

  return (
    <div className="relative flex flex-col justify-between gap-6 size-full">
      <button
        type="button"
        aria-label="Dismiss pending screen"
        onClick={onDismiss}
        className="absolute top-4 right-4 z-20 p-2 rounded-full text-token-gray-900 hover:bg-token-gray-200 transition-colors"
      >
        <XIcon className="size-5" />
      </button>

      <div className="pt-6 mx-6">
        <div className="flex flex-row py-8 justify-center relative" aria-hidden="true">
          <TokenIconElement address={sellToken.address} chainId={sellToken.chainId} left />
          <div className="flex items-center">
            <ConnectorElement />
          </div>
          <TokenIconElement address={buyToken.address} chainId={buyToken.chainId} />
        </div>

        <div className="flex flex-col items-center gap-1.5">
          <div className={fontStyles({ variant: 'H5' })}>{statusCopy.title}</div>
          <div
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['text-token-gray-800', 'text-center'],
            })}
          >
            {statusCopy.body}
          </div>
          <div
            className={classNames(
              'w-full mt-6 rounded-lg border border-token-gray-300 p-4',
              'flex flex-col gap-3 text-token-gray-800',
            )}
          >
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: ['flex', 'items-center', 'justify-between', 'gap-4'],
              })}
            >
              <span>Estimated Time Remaining</span>
              <span className="text-token-gray-1000">{remainingLabel}</span>
            </div>
            <div className="h-px bg-token-gray-300" />
            <div
              className={fontStyles({
                variant: 'BODY_MD',
                class: ['flex', 'items-center', 'justify-between', 'gap-4'],
              })}
            >
              <span>Elapsed Time</span>
              <span className="text-token-gray-1000">{elapsedLabel}</span>
            </div>
          </div>
          <div
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['text-token-gray-700', 'text-center', 'pt-2'],
            })}
          >
            Leaving this state won&apos;t terminate the trade.
          </div>
        </div>
      </div>

      <div className="flex w-full gap-2.5 px-6 pb-4 desktop:pb-6">
        <button
          type="button"
          className={buttons({
            size: 'large',
            color: 'gray',
            shape: 'pill',
            class: 'animate-[fadeIn_500ms_cubic-bezier(0.4,0,0,1)]',
          })}
          onClick={handleSeeDetails}
        >
          See details
        </button>
        <button
          type="button"
          className={buttons({
            size: 'large',
            color: 'black',
            shape: 'pill',
            class: 'animate-[fadeIn_500ms_cubic-bezier(0.4,0,0,1)]',
          })}
          onClick={onDismiss}
        >
          New trade
        </button>
      </div>
    </div>
  );
};
